import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { EligibilityEvaluation } from '@plentifulapp/plentiful-wizard-engine';
import { MaterialIcons } from '@expo/vector-icons';
import { logError } from '../../utils/logger';
import { useLocalization } from '../../store/utils/useLocalization';
import Svg from '../Svg';
import FilledButton from '../FilledButton';
import { pantryDetailsPromptStyleSheet } from '../profile/pantryDetailsPromptStyleSheet';
import { StateProgramWizard } from './StateProgramWizard';
import { useStateProgramContext } from './StateProgramContext';
import StateProgramFacade from './StateProgramFacade';
import { StateProgramFormStep } from './StateProgramFormStep';
import { StyledModal } from '../StyledModal';
import Colors from '../../constants/Colors';
import { LoadingSkeleton } from '../LoadingSkeleton';
import StateProgramsError from './errors/StateProgramsError';

interface PantryDetailsPromptProps {
  /**
   * Visit ID at the current pantry.
   * Optional. Absent indicates that the user does not have a scheduled visit
   * and can't perform the attestation yet.
   */
  visitId?: number;
  /**
   * provider ID of the current pantry.
   */
  providerId: number;
  /**
   * state that the current pantry is located
   */
  providerState: string;
  /**
   * User registration state. Should include the user ID if logged in.
   */
  registration?: {
    user?: {
      id?: number;
      [key: string]: any;
    };
    [key: string]: any;
  };
  /**
   * Requests that the caller close the current modal, but without advancing to
   * the next registration modal (such as when we have other modals to show next).
   * Should be absent if not showing in modal.
   */
  closePromptModal?: () => void;
  /**
   * Requests that the caller close the current modal, and indicates that we are
   * not going to show another modal for this program, so the caller is free
   * to advance to the next program.
   * Should be absent if not showing in modal.
   */
  nextModal?: () => void;
  /**
   * Consumer for errors that need to be logged and reported.
   * Optional. Defaults to {@link logError} with reports to Sentry.
   */
  errorReporter?: typeof logError;
}

type ProgramModalMode = 'none' | 'promptModal' | 'wizardModal';

interface EligibilityStatusDisplay {
  iconName: string;
  iconColor: string;
  description: (
    t: (key: string, params?: any) => string,
    programName: string,
    ineligibleReason?: string
  ) => string;
  callToAction: (openWizardModal: () => void, t: (key: string, params?: any) => string) => React.ReactNode;
}

/**
 * Renders the state program prompt UI for a pantry details screen.
 */
function PantryDetailsProgramPromptContent({
  programServices,
  ...props
}: PantryDetailsPromptProps & { programServices: StateProgramFacade }): React.ReactNode {
  const { locale, t } = useLocalization();
  const [programModal, setProgramModal] = useState<ProgramModalMode>('none');
  const errorReporter = props.errorReporter ?? logError;
  const [eligibility, setEligibility] = useState<EligibilityEvaluation>({ result: 'unknown' });
  const [loadingAttestation, setLoadingAttestation] = useState<boolean>(false);

  const programAvailableInState = programServices.isAvailableInState(props.providerState);

  // Use ref to track the visit we've already loaded attestation for
  const loadedVisitIdRef = useRef<number | null>(null);

  const loadAttestation = useCallback(
    async (forceReload = false) => {
      // Skip if no visitId or if we've already loaded this visit and aren't forcing a reload
      if (!props.visitId || (!forceReload && props.visitId === loadedVisitIdRef.current)) {
        return;
      }

      setLoadingAttestation(true);
      try {
        const result = await programServices.getAttestation(props.visitId);

        if (result === null) {
          // No attestation exists yet, set to unknown status
          setEligibility({ result: 'unknown' });
        } else {
          // We have an attestation response
          setEligibility({
            result: result.eligibilityStatus,
            ineligibleReasons: result.eligibilityReason,
          });
        }

        // Track that we've loaded this visit
        loadedVisitIdRef.current = props.visitId;
      } catch (err) {
        // Only actual errors reach here now (not 404s)
        errorReporter(
          new StateProgramsError(
            'Failed to load attestation',
            err,
            programServices.id,
            undefined,
            props.visitId,
            props.providerId
          )
        );
      } finally {
        setLoadingAttestation(false);
      }
    },
    [props.visitId, programServices, errorReporter]
  );

  useEffect(() => {
    loadAttestation(); // Load attestation when visit ID changes
  }, [loadAttestation]);

  useEffect(() => {
    // let caller show the prompt modal but not show or hide the wizard
    // (only user button click in the prompt can do that)
    if (props.closePromptModal && programModal === 'none') {
      setProgramModal('promptModal');
    } else if (!props.closePromptModal && programModal === 'promptModal') {
      setProgramModal('none');
    }
  }, [props.closePromptModal, programModal]);

  const closeWizardModal = () => {
    // close the wizard
    setProgramModal('none');

    // Force reload attestation data to get the latest state
    loadAttestation(true);

    // only advance to next modal if the wizard was opened from a modal prompt
    if (props.nextModal) {
      props.nextModal();
    }
    // if inline prompt was used there's no need for next modal
  };
  const openWizardModal = () => {
    // close the prompt modal (without moving to the next program)
    if (programModal === 'promptModal') {
      props.closePromptModal!();
    }
    setProgramModal('wizardModal');
  };

  // Extract the ineligible reason outside the switch to avoid case declarations
  const ineligibleReason =
    eligibility.result === 'ineligible' && eligibility.ineligibleReasons
      ? eligibility.ineligibleReasons[locale]
      : '';

  // shared edit icon component for eligibilities that allow it
  const editIconCallToAction = (onPress: () => void) => (
    <TouchableOpacity onPress={onPress}>
      <MaterialIcons name={'edit'} size={24} color={'gray'} />
    </TouchableOpacity>
  );

  // Define all eligibility statuses with their properties
  const eligibilityStatuses: Record<string, EligibilityStatusDisplay> = {
    ineligible: {
      iconName: 'unverified',
      iconColor: pantryDetailsPromptStyleSheet.icon.color,
      description: (t, programName, ineligibleReason) =>
        t('program_ineligible', {
          program: programName,
          reason: ineligibleReason,
        }),
      callToAction: (openWizardModal, t) => (
        <FilledButton
          onPress={openWizardModal}
          color={pantryDetailsPromptStyleSheet.button.color}
          label={t('tefap_retake')}
          full={false}
          paddingHorizontal={pantryDetailsPromptStyleSheet.button.paddingHorizontal}
        />
      ),
    },
    eligible: {
      iconName: 'verified',
      iconColor: Colors.accentColor,
      description: (t, programName) => t('program_eligible', { program: programName }),
      callToAction: editIconCallToAction,
    },
    unknown: {
      iconName: 'unverified',
      iconColor: pantryDetailsPromptStyleSheet.icon.color,
      description: (t, programName) => t('pantry_details_prompt_visit_label', { programName }),
      callToAction: (openWizardModal, t) => (
        <FilledButton
          onPress={openWizardModal}
          color={pantryDetailsPromptStyleSheet.button.color}
          label={t('program_complete_now')}
          full={false}
          paddingHorizontal={pantryDetailsPromptStyleSheet.button.paddingHorizontal}
        />
      ),
    },
    notApplicable: {
      iconName: 'verified',
      iconColor: Colors.accentColor,
      description: (translate, programName) => translate('program_not_applicable', { program: programName }),
      callToAction: editIconCallToAction,
    },
  };

  // Get the eligibility status or default to 'unknown'
  const status = eligibilityStatuses[eligibility.result] || eligibilityStatuses.unknown;

  // Extract properties from the status
  const programIconName = status.iconName;
  const programIconColor = status.iconColor;
  const programDescription = status.description(t, programServices.name, ineligibleReason);
  const programCallToAction = status.callToAction(openWizardModal, t);

  return loadingAttestation ? (
    <View
      testID={`PantryDetailsProgramPrompt-${programServices.id}`}
      style={pantryDetailsPromptStyleSheet.view}
    >
      <View style={pantryDetailsPromptStyleSheet.container} testID={`LoadingSkeleton`}>
        <View style={pantryDetailsPromptStyleSheet.leftContainer}>
          <LoadingSkeleton
            style={{
              width: pantryDetailsPromptStyleSheet.icon.width,
              height: pantryDetailsPromptStyleSheet.icon.height,
              borderRadius: pantryDetailsPromptStyleSheet.icon.width,
            }}
          />
          <View style={pantryDetailsPromptStyleSheet.promptContainer}>
            <LoadingSkeleton
              style={{
                height: pantryDetailsPromptStyleSheet.promptLabel.fontSize,
                ...pantryDetailsPromptStyleSheet.skeletonLine1,
              }}
            />
            <LoadingSkeleton
              style={{
                height: pantryDetailsPromptStyleSheet.promptLabel.fontSize,
                ...pantryDetailsPromptStyleSheet.skeletonLine2,
              }}
            />
            <LoadingSkeleton
              style={{
                height: pantryDetailsPromptStyleSheet.promptLabel.fontSize,
                ...pantryDetailsPromptStyleSheet.skeletonLine3,
              }}
            />
          </View>
        </View>
      </View>
    </View>
  ) : (
    <View
      testID={`PantryDetailsProgramPrompt-${programServices.id}`}
      style={pantryDetailsPromptStyleSheet.view}
    >
      <View style={pantryDetailsPromptStyleSheet.container}>
        <View style={pantryDetailsPromptStyleSheet.leftContainer}>
          <Svg
            icon={programIconName}
            color={programIconColor}
            width={pantryDetailsPromptStyleSheet.icon.width}
            height={pantryDetailsPromptStyleSheet.icon.height}
          />
          <View style={pantryDetailsPromptStyleSheet.promptContainer}>
            <Text style={pantryDetailsPromptStyleSheet.text}>
              {t('pantry_details_prompt_title', {
                programName: programServices.name,
              })}
            </Text>
            <View style={pantryDetailsPromptStyleSheet.promptLabelContainer}>
              <Text style={pantryDetailsPromptStyleSheet.promptLabel}>
                {props.visitId && programAvailableInState
                  ? programDescription
                  : t('pantry_details_prompt_without_visit_label', {
                      programName: programServices.name,
                    })}
              </Text>
            </View>
          </View>
        </View>
        {props.visitId && props.registration?.user?.id && (
          <View style={pantryDetailsPromptStyleSheet.buttonContainer}>
            {programAvailableInState && programCallToAction}
            <StyledModal
              testID={`PantryDetailsProgramPrompt-${programServices.id}-RegistrationModal`}
              isVisible={
                /**
                 * Modals like this (and the built-in one that it wraps) have their own
                 * visibility controls to preserve state and facilitate animation.
                 * So this flag is used to control rendering rather than
                 * conditional rendering here in the parent container.
                 * See https://www.npmjs.com/package/react-native-modal#a-complete-example
                 */
                programModal === 'promptModal'
              }
              headerText={t('pantry_details_prompt_title', {
                programName: programServices.name,
              })}
              onClose={props.nextModal}
              buttons={[
                {
                  localeKey: 'program_complete_later',
                  onPress: props.nextModal!,
                },
                {
                  localeKey: 'program_complete_now',
                  onPress: openWizardModal,
                  filled: true,
                },
              ]}
            >
              <Text>{t('pantry_details_prompt_visit_label', { programName: programServices.name })}</Text>
            </StyledModal>
            <StateProgramWizard
              isVisible={
                /**
                 * This wraps a similar modal and so it
                 * also uses the flag rather than conditional rendering.
                 * The handlers and state enum should ensure that the modals are mutually exclusive.
                 */
                programModal === 'wizardModal'
              }
              onClose={closeWizardModal}
              userId={props.registration.user.id}
              visitId={props.visitId}
              providerId={props.providerId}
              formFactory={StateProgramFormStep}
            />
          </View>
        )}
      </View>
    </View>
  );
}

/**
 * Displays a state program enrollment prompt on the pantry details screen.
 * Reads program services from {@link StateProgramContextProvider} and renders nothing
 * if the program is not available in the current app bundle.
 */
function PantryDetailsProgramPrompt(props: PantryDetailsPromptProps): React.ReactNode {
  const programServices = useStateProgramContext();
  if (!programServices) return null;
  return <PantryDetailsProgramPromptContent {...props} programServices={programServices} />;
}

export default PantryDetailsProgramPrompt;
